import React from 'react';
import { CONTENT_TYPES } from '../constants/contentTypes';
import {
  DEFAULT_VOLUME,
  PLAYER_IN_LOCALSTORAGE,
  PLAYER_PREFERENCES,
} from '../constants/player';

const volumeAsPercentage = (currentValue) => {
  return Math.round(currentValue * 100);
};

const isPlayerReady = (currentPlayer) =>
  currentPlayer && currentPlayer.player_ !== null;

const updateLocalStorage = (currentPlayer, currentValue) => {
  if (currentPlayer?.volume() || currentValue === 0) {
    PLAYER_PREFERENCES.volumeLevel = currentValue;
    localStorage.setItem(
      PLAYER_IN_LOCALSTORAGE,
      JSON.stringify(PLAYER_PREFERENCES),
    );
  }
};

const drawVolumeLevel = (currentPlayer, currentValue) => {
  const volumeLevelDiv =
    currentPlayer?.controlBar?.volumePanel?.volumeControl?.volumeBar
      ?.volumeLevel;

  if (volumeLevelDiv) {
    volumeLevelDiv.el_.style.width = currentValue + '%';
  }
};

const updateARIAAttributes = (currentPlayer, currentValue) => {
  if (!isPlayerReady(currentPlayer)) return;

  currentPlayer.volume(currentValue);
  const ariaValue = currentPlayer.muted()
    ? 0
    : volumeAsPercentage(currentValue);
  currentPlayer.el_?.setAttribute('aria-valuenow', ariaValue);
  currentPlayer.el_?.setAttribute('aria-valuetext', ariaValue + '%');

  updateLocalStorage(currentPlayer, currentValue);
  drawVolumeLevel(currentPlayer, ariaValue);
};

const applyMutedPreference = (currentPlayer) => {
  if (!isPlayerReady(currentPlayer)) return;

  currentPlayer.volume(DEFAULT_VOLUME);
  currentPlayer.muted(true);
  currentPlayer.el_?.setAttribute('aria-valuenow', 0);
  currentPlayer.el_?.setAttribute('aria-valuetext', '0%');
  updateLocalStorage(currentPlayer, 0);
  drawVolumeLevel(currentPlayer, 0);
};

export function usePlayerPreferencesUpdate(currentPlayer) {
  React.useEffect(() => {
    if (!currentPlayer) return;

    const handleVolumeChange = () => {
      updateARIAAttributes(
        currentPlayer,
        currentPlayer.muted() ? 0 : currentPlayer.volume(),
      );
    };

    currentPlayer.ready(() => {
      currentPlayer.on('volumechange', handleVolumeChange);
    });

    return () => {
      currentPlayer.off('volumechange', handleVolumeChange);
    };
  }, [currentPlayer]);
}

export function usePlayerPreferencesInit(
  currentPlayer,
  isEpgSection = false,
  selectedContent,
) {
  const playerPreferences = JSON.parse(
    localStorage.getItem(PLAYER_IN_LOCALSTORAGE),
  );

  React.useEffect(() => {
    if (!currentPlayer) return;

    const applyPreferences = () => {
      if (!isPlayerReady(currentPlayer)) return;

      if (playerPreferences && !(Object.keys(playerPreferences).length === 0)) {
        if (playerPreferences.volumeLevel === 0) {
          applyMutedPreference(currentPlayer);
        } else {
          updateARIAAttributes(currentPlayer, playerPreferences.volumeLevel);
        }
      } else {
        if (
          isEpgSection ||
          selectedContent?.contentType === CONTENT_TYPES.BROADCAST
        ) {
          currentPlayer.volume(DEFAULT_VOLUME);
        } else {
          updateARIAAttributes(currentPlayer, DEFAULT_VOLUME);
        }
      }
    };

    currentPlayer.ready(applyPreferences);
  }, [currentPlayer, selectedContent]);
}
